import React, { useState } from 'react';
// Native MUI Components
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Typography, 
  Button, 
  Chip, 
  Box, 
  Divider,
  Collapse,
  Stack,
  IconButton,
  Grid
} from '@mui/material';
import { 
  Person as User, 
  Edit 
} from '@mui/icons-material';
import { Traveller, Trip, Apartment } from '@/types';
import EditTravellerForm from './EditTravellerForm';
import MemberStatusChip from '@/components/ui/MemberStatusChip';
import { calculateAge } from '@/utils/calculations';

interface TravellerCardProps {
  traveller: Traveller;
  trip: Trip;
  apartments: Apartment[];
}

const TravellerCard: React.FC<TravellerCardProps> = ({ traveller, trip, apartments }) => {
  const [viewDetails, setViewDetails] = useState(false);

  const apartment = apartments.find(a => a.id === traveller.apartmentId);
  const skiPass = trip.skiPasses.find(s => s.id === traveller.skiPassType);
  const transportation = trip.transportationOptions.find(t => t.id === traveller.transportationType);

  return (
    <Card sx={{ 
      transition: 'box-shadow 0.3s ease-in-out',
      '&:hover': {
        boxShadow: (theme) => theme.shadows[8]
      }
    }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <User sx={{ color: 'primary.main' }} />
            {traveller.name}
          </Box>
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <MemberStatusChip isMember={traveller.isSkiClubMember} />
            <EditTravellerForm
              traveller={traveller}
              trip={trip}
              trigger={
                <IconButton size="small" sx={{ p: 1 }}>
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              }
            />
          </Stack>
        }
        sx={{ pb: 1 }}
      />
      <CardContent>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid size={6}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                🎂 Alter:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {calculateAge(traveller.dateOfBirth)} Jahre
              </Typography>
            </Box>
            </Grid>
            <Grid size={6}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                📧 E-Mail:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {traveller.email}
              </Typography>
            </Box>
            </Grid>
            <Grid size={6}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                🏠 Apartment:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {apartment ? `${apartment.name} (${apartment.currentOccupancy}/${apartment.maxOccupancy})` : 'Kein Apartment'}
              </Typography>
            </Box>
            </Grid>
            <Grid size={6}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                🎿 Skipass:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {skiPass ? skiPass.name : 'Kein Skipass'}
              </Typography>
            </Box>
            </Grid>
            <Grid size={12}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                🚌 Transport:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {transportation ? transportation.name : 'Kein Transport'}
              </Typography>
            </Box>
            </Grid>
          </Grid>
          
          <Divider />
          
          <Button
            variant="outlined"
            size="small"
            onClick={() => setViewDetails(!viewDetails)}
            fullWidth
          >
            {viewDetails ? 'Details schließen' : 'Details anzeigen'}
          </Button>

          <Collapse in={viewDetails}>
            <Box sx={{ pt: 2 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    📞 Telefon:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{traveller.phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    🏠 Adresse:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {traveller.address.street}, {traveller.address.postalCode} {traveller.address.city}, {traveller.address.country}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    🎂 Geburtsdatum:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(traveller.dateOfBirth).toLocaleDateString('de-DE')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TravellerCard;
